import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Mail, Lock, Eye, EyeClosed, ArrowRight } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import shomriLogoWhite from "@/assets/shomri-logo-white.png";

interface SignInCardProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  isLoading?: boolean;
  logo?: string;
  title?: string;
  subtitle?: string;
}

export function SignInCard({ 
  onSubmit, 
  isLoading = false, 
  logo,
  title = "Welcome Back",
  subtitle = "Sign in to continue to Shomri"
}: SignInCardProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  // For 3D card effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [10, -10]);
  const rotateY = useTransform(mouseX, [-300, 300], [-10, 10]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(email, password);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-[#0a0a1f] via-[#24284F] to-[#1a1a30]">
      {/* Background gradient effect - Shomri Navy based */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#8B76EE]/20 via-transparent to-transparent" />
      
      {/* Subtle noise texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Top radial glow - Shomri Purple */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#8B76EE]/10 rounded-full blur-[120px]" />

      {/* Animated glow spots - Shomri Purple and Pink */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#8B76EE]/20 rounded-full blur-[100px]"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#EB709F]/15 rounded-full blur-[120px]"
      />

      <div 
        className="relative z-10 w-full max-w-md px-4"
        style={{ perspective: "1000px" }}
      >
        <motion.div
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ rotateX, rotateY }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="relative"
        >
          {/* Card glow effect */}
          <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
            {/* Traveling light beam effect - Shomri Purple and Pink */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0"
            >
              {/* Top light beam */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-[2px] bg-gradient-to-r from-transparent via-[#8B76EE]/60 to-transparent blur-sm" />
              {/* Right light beam */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 h-32 w-[2px] bg-gradient-to-b from-transparent via-[#EB709F]/60 to-transparent blur-sm" />
              {/* Bottom light beam */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-[2px] bg-gradient-to-r from-transparent via-[#8B76EE]/60 to-transparent blur-sm" />
              {/* Left light beam */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-32 w-[2px] bg-gradient-to-b from-transparent via-[#EB709F]/60 to-transparent blur-sm" />
            </motion.div>

            {/* Card border glow */}
            <div className="absolute inset-0 rounded-2xl border border-white/10" />
          </div>

          {/* Glass card background */}
          <div className="relative rounded-2xl bg-white/[0.03] backdrop-blur-xl p-8 shadow-2xl">
            {/* Subtle card inner patterns */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.05] via-transparent to-transparent" />

            {/* Logo and header */}
            <div className="relative flex flex-col items-center mb-8">
              <img 
                src={logo || shomriLogoWhite} 
                alt="Shomri Logo" 
                className="w-40 h-auto object-contain mb-4"
              />

              <h1 className="text-2xl font-bold text-white mb-2">
                {title}
              </h1>
              
              <p className="text-white/50 text-sm text-center">
                {subtitle}
              </p>
            </div>

            {/* Login form */}
            <form onSubmit={handleSubmit} className="relative space-y-4">
              {/* Email input */}
              <div className="space-y-2">
                <label className="text-white/70 text-sm font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedInput("email")}
                    onBlur={() => setFocusedInput(null)}
                    className={cn(
                      "w-full bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 pl-10 pr-3 transition-all duration-300",
                      focusedInput === "email" && "border-[#8B76EE]/50 bg-white/10"
                    )}
                  />
                  {focusedInput === "email" && (
                    <motion.div
                      layoutId="inputHighlight"
                      className="absolute inset-0 rounded-md ring-2 ring-[#8B76EE]/30 pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    />
                  )}
                </div>
              </div>

              {/* Password input */}
              <div className="space-y-2">
                <label className="text-white/70 text-sm font-medium">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedInput("password")}
                    onBlur={() => setFocusedInput(null)}
                    className={cn(
                      "w-full bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 pl-10 pr-10 transition-all duration-300",
                      focusedInput === "password" && "border-[#8B76EE]/50 bg-white/10"
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? (
                      <EyeClosed className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                  {focusedInput === "password" && (
                    <motion.div
                      layoutId="inputHighlight"
                      className="absolute inset-0 rounded-md ring-2 ring-[#8B76EE]/30 pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    />
                  )}
                </div>
              </div>

              {/* Remember me & Forgot password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={() => setRememberMe(!rememberMe)}
                      className="appearance-none h-4 w-4 rounded border border-white/20 bg-white/5 checked:bg-[#8B76EE] checked:border-[#8B76EE] focus:outline-none focus:ring-1 focus:ring-[#8B76EE]/30 transition-all duration-200 cursor-pointer"
                    />
                    {rememberMe && (
                      <svg
                        className="absolute inset-0 w-4 h-4 text-white pointer-events-none"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-white/50 text-sm group-hover:text-white/70 transition-colors">
                    Remember me
                  </span>
                </label>

                <Link
                  to="/forgot-password"
                  className="text-[#8B76EE]/80 text-sm hover:text-[#8B76EE] transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Sign in button */}
              <div className="relative pt-2">
                {/* Button glow effect - Shomri Purple to Pink */}
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#8B76EE] to-[#EB709F] blur-xl opacity-50" />

                <motion.button
                  type="submit"
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative w-full h-11 rounded-lg bg-gradient-to-r from-[#8B76EE] to-[#EB709F] text-white font-medium shadow-lg shadow-[#8B76EE]/25 hover:shadow-[#8B76EE]/40 transition-shadow disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden"
                >
                  {/* Button background animation */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#8B76EE] to-[#EB709F]"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.5 }}
                  />
                  <span className="relative flex items-center justify-center gap-2">
                    {isLoading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </span>
                </motion.button>
              </div>

              {/* Sign up link */}
              <p className="text-center text-white/50 text-sm pt-4">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-[#8B76EE] hover:text-[#EB709F] transition-colors font-medium"
                >
                  Sign up
                </Link>
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
